import Button from '@mui/material/Button';
import Check from '@mui/icons-material/Check';
import FilterList from '@mui/icons-material/FilterList';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../../api/client';
import type { LibraryKind } from '../../api/types';
import { useApi } from '../../api/useApi';

interface LibraryFilterProps {
  /// The kinds of library whose contents this screen shows. A library of another kind is not
  /// offered, because choosing it could only ever empty the screen.
  serves: LibraryKind[];
  value: string | undefined;
  onChange: (libraryId: string | undefined) => void;
}

/// Which library a screen is showing. Quiet by design: on a server with one library there is
/// nothing to choose between, and the control takes itself away rather than asking a question
/// with one answer.
export default function LibraryFilter({ serves, value, onChange }: LibraryFilterProps) {
  const { t } = useTranslation();
  const libraries = useApi(['libraries'], api.libraries);
  const [anchor, setAnchor] = useState<HTMLElement>();

  const choices = (libraries.data ?? []).filter((library) => serves.includes(library.kind));

  if (choices.length < 2) {
    return null;
  }

  const chosen = choices.find((library) => library.id === value);

  return (
    <>
      <Button
        size="small"
        color="inherit"
        startIcon={<FilterList fontSize="small" />}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{ color: chosen ? 'primary.main' : 'text.secondary' }}
      >
        {chosen?.name ?? t('items.allLibraries')}
      </Button>

      <Menu anchorEl={anchor} open={anchor !== undefined} onClose={() => setAnchor(undefined)}>
        <MenuItem
          selected={chosen === undefined}
          onClick={() => {
            onChange(undefined);
            setAnchor(undefined);
          }}
        >
          <ListItemIcon>{chosen === undefined && <Check fontSize="small" />}</ListItemIcon>
          {t('items.allLibraries')}
        </MenuItem>

        {choices.map((library) => (
          <MenuItem
            key={library.id}
            selected={library.id === value}
            onClick={() => {
              onChange(library.id);
              setAnchor(undefined);
            }}
          >
            <ListItemIcon>{library.id === value && <Check fontSize="small" />}</ListItemIcon>
            {library.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
